/**
 * Maps a failed microphone / camera / screen-share action to an i18n key.
 *
 * The Stream SDK surfaces the browser's own getUserMedia / getDisplayMedia
 * DOMExceptions, so the `name` tells us what the user can actually do about it:
 * grant permission, plug in / free up a device, or just retry.
 */
export type DeviceKind = "microphone" | "camera" | "screenShare";

const PERMISSION_ERRORS = new Set([
  "NotAllowedError",
  "PermissionDeniedError",
  "SecurityError",
]);

const UNAVAILABLE_ERRORS = new Set([
  "NotFoundError",
  "DevicesNotFoundError",
  "OverconstrainedError",
  "ConstraintNotSatisfiedError",
  "NotReadableError",
  "TrackStartError",
  "AbortError",
]);

const errorName = (err: unknown): string | undefined => {
  if (typeof err !== "object" || err === null) return undefined;
  const name = (err as { name?: unknown }).name;
  return typeof name === "string" ? name : undefined;
};

/** True when the user dismissed the browser's own permission / picker prompt. */
export const isPermissionDenied = (err: unknown) =>
  PERMISSION_ERRORS.has(errorName(err) ?? "");

/**
 * Returns the `call.*` translation key describing why the action failed, or
 * `null` when no feedback should be shown (the user cancelled the screen-share
 * picker themselves — that is a choice, not an error).
 */
export const deviceErrorMessageKey = (
  err: unknown,
  kind: DeviceKind,
): string | null => {
  const name = errorName(err) ?? "";

  if (kind === "screenShare") {
    return PERMISSION_ERRORS.has(name) ? null : "call.screen_share_failed";
  }

  const prefix = kind === "microphone" ? "call.mic" : "call.camera";
  if (PERMISSION_ERRORS.has(name)) return `${prefix}_permission_denied`;
  if (UNAVAILABLE_ERRORS.has(name)) return `${prefix}_unavailable`;
  return `${prefix}_toggle_failed`;
};
